import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useBandwidth } from '../hooks/useBandwidth';
import { usePricing } from '../hooks/usePricing';
import { createPayment } from '../services/paymentService';

const BandwidthPackagesPage = () => {
  const navigate = useNavigate();
  const { user, profile } = useAuth();
  const {
    packages,
    stats,
    loading,
    pausePackage,
    resumePackage,
    purchasePackage,
  } = useBandwidth();
  const { allTiers, calculatePrice, loading: pricingLoading } = usePricing();

  const [showPurchaseModal, setShowPurchaseModal] = useState(false);
  const [selectedPackageType, setSelectedPackageType] = useState(null);
  const [purchaseAmount, setPurchaseAmount] = useState(10);
  const [purchaseInputValue, setPurchaseInputValue] = useState('10');
  const [isPurchasing, setIsPurchasing] = useState(false);
  const [purchaseError, setPurchaseError] = useState('');
  const [selectedDuration, setSelectedDuration] = useState('no_expiry');

  const monthlyPricingTiers = [
    { minGb: 1, maxGb: 4, d30: 1.8000, d7: 1.6000, d1: 1.4000 },
    { minGb: 5, maxGb: 9, d30: 1.6920, d7: 1.5040, d1: 1.3160 },
    { minGb: 10, maxGb: 49, d30: 1.6110, d7: 1.4320, d1: 1.2530 },
    { minGb: 50, maxGb: 99, d30: 1.4490, d7: 1.2880, d1: 1.1270 },
    { minGb: 100, maxGb: 249, d30: 1.2870, d7: 1.1440, d1: 1.0010 },
    { minGb: 250, maxGb: 499, d30: 1.0710, d7: 0.9520, d1: 0.8330 },
    { minGb: 500, maxGb: 999, d30: 0.8820, d7: 0.7840, d1: 0.6860 },
  ];

  const durationOptions = [
    { key: 'd1', label: '1 Day' },
    { key: 'd7', label: '7 Days' },
    { key: 'd30', label: '30 Days' },
    { key: 'no_expiry', label: 'No Expiry' },
  ];

  const getMonthlyPrice = (gb, duration) => {
    const tier = monthlyPricingTiers.find(t => gb >= t.minGb && gb <= t.maxGb);
    if (!tier) {
      if (gb >= 500) return monthlyPricingTiers[monthlyPricingTiers.length - 1][duration];
      return monthlyPricingTiers[0][duration];
    }
    return tier[duration];
  };

  const availablePackages = [
    {
      type: 'monthly_residential',
      name: 'MONTHLY RESIDENTIAL',
      displayType: 'MONTHLY RESIDENTIAL',
      description: 'RESIDENTIAL PROXIES WITH EXPIRY-BASED PRICING - CHOOSE YOUR DURATION FOR BETTER RATES',
      isMonthly: true,
    },
    {
      type: 'residential_basic',
      name: 'ROTATING RESIDENTIAL BASIC',
      displayType: 'RESIDENTIAL BASIC',
      description: 'HIGH QUALITY ROTATING RESIDENTIAL PROXIES - PERFECT FOR MOST USE CASES WITH GREAT RELIABILITY',
    },
    {
      type: 'residential_premium',
      name: 'ROTATING RESIDENTIAL PREMIUM',
      displayType: 'RESIDENTIAL PREMIUM',
      description: 'PREMIUM ROTATING RESIDENTIAL PROXIES - THE HIGHEST QUALITY PROVIDERS FOR DEMANDING TASKS',
    },
  ];

  const handlePauseResume = async (pkg) => {
    if (pkg.status === 'active') {
      await pausePackage(pkg.id);
    } else {
      await resumePackage(pkg.id);
    }
  };

  const openPurchaseModal = (packageType) => {
    setSelectedPackageType(packageType);
    setPurchaseAmount(10);
    setPurchaseInputValue('10');
    setPurchaseError('');
    setSelectedDuration(packageType === 'monthly_residential' ? 'd1' : 'no_expiry');
    setShowPurchaseModal(true);
  };

  const handlePurchaseInputChange = (e) => {
    const value = e.target.value;
    setPurchaseInputValue(value);

    const numValue = parseInt(value);
    if (!isNaN(numValue) && numValue >= 1) {
      setPurchaseAmount(Math.min(numValue, 10000));
    }
  };

  const handlePurchaseInputBlur = () => {
    const numValue = parseInt(purchaseInputValue);
    if (isNaN(numValue) || numValue < 1) {
      setPurchaseAmount(1);
      setPurchaseInputValue('1');
    } else {
      const clampedValue = Math.min(numValue, 10000);
      setPurchaseAmount(clampedValue);
      setPurchaseInputValue(String(clampedValue));
    }
  };

  const handlePurchase = async () => {
    if (!selectedPackageType) return;

    setIsPurchasing(true);
    setPurchaseError('');

    const isMonthly = selectedPackageType === 'monthly_residential';

    if (isMonthly && selectedDuration !== 'no_expiry') {
      const pricePerGb = getMonthlyPrice(purchaseAmount, selectedDuration);
      const totalPrice = pricePerGb * purchaseAmount;
      const durationDays = selectedDuration === 'd1' ? 1 : selectedDuration === 'd7' ? 7 : 30;

      const result = await createPayment({
        proxyType: 'monthly_residential',
        gbAmount: purchaseAmount,
        priceUsd: totalPrice,
        pricePerGb,
        durationDays,
      });

      if (result.success && result.paymentUrl) {
        setShowPurchaseModal(false);
        window.location.href = result.paymentUrl;
      } else {
        setPurchaseError(result.error || 'Failed to create payment');
        setIsPurchasing(false);
      }
    } else {
      const result = await purchasePackage(selectedPackageType, purchaseAmount);

      if (result.success && result.paymentUrl) {
        setShowPurchaseModal(false);
        window.location.href = result.paymentUrl;
      } else {
        setPurchaseError(result.error || 'Failed to create payment');
        setIsPurchasing(false);
      }
    }
  };

  const getTiersForPackage = (type) => {
    return allTiers[type] || [];
  };

  const formatTierRange = (tier) => {
    if (tier.maxGb) {
      return `${tier.minGb} - ${tier.maxGb} GB`;
    }
    return `${tier.minGb}+ GB`;
  };

  // Check if amount falls within a tier range
  const isActiveTier = (tier, amount) => {
    if (tier.maxGb) {
      return amount >= tier.minGb && amount <= tier.maxGb;
    }
    return amount >= tier.minGb;
  };

  // Get all pricing tiers for the modal (extended list up to 10TB)
  const getExtendedTiers = (type) => {
    const baseTiers = allTiers[type] || [];
    // Filter out tiers with minGb === 0
    return baseTiers.filter(tier => tier.minGb > 0);
  };

  return (
    <div className="bandwidth-packages-page">
      {/* Breadcrumb */}
      <div className="breadcrumb">
        <span>User Bandwidth Packages</span>
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <polyline points="9,18 15,12 9,6"/>
        </svg>
        <span className="current">Bandwidth Packages</span>
      </div>

      <h1 className="page-title">Bandwidth Packages</h1>

      {/* Header Card */}
      <div className="bandwidth-header-card">
        <div className="header-left">
          <div className="header-icon">
            <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
              <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"/>
              <polyline points="3.27,6.96 12,12.01 20.73,6.96"/>
              <line x1="12" y1="22.08" x2="12" y2="12"/>
            </svg>
          </div>
          <div className="header-text">
            <h2>BANDWIDTH PACKAGES</h2>
            <p><span className="dot"></span> MANAGE YOUR BANDWIDTH AND PACKAGES</p>
          </div>
        </div>
              </div>

      {/* Stats Grid */}
      <div className="bandwidth-stats-grid">
        <div className="bandwidth-stat-card">
          <div className="stat-icon">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
              <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"/>
            </svg>
          </div>
          <div className="stat-content">
            <span className="stat-label">TOTAL PACKAGES</span>
            <span className="stat-value">{loading ? '...' : stats.totalPackages}</span>
          </div>
        </div>

        <div className="bandwidth-stat-card">
          <div className="stat-icon">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
              <path d="M12 2v20M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/>
            </svg>
          </div>
          <div className="stat-content">
            <span className="stat-label">TOTAL PURCHASED</span>
            <span className="stat-value">{loading ? '...' : stats.totalPurchased.toFixed(2)} GB</span>
          </div>
        </div>

        <div className="bandwidth-stat-card">
          <div className="stat-icon yellow">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
              <path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z"/>
            </svg>
          </div>
          <div className="stat-content">
            <span className="stat-label">TOTAL REMAINING</span>
            <span className="stat-value">{loading ? '...' : stats.totalRemaining.toFixed(2)} GB</span>
          </div>
        </div>

        <div className="bandwidth-stat-card">
          <div className="stat-icon">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
              <path d="M3 3v18h18"/>
              <path d="M18 17V9"/>
              <path d="M13 17V5"/>
              <path d="M8 17v-3"/>
            </svg>
          </div>
          <div className="stat-content">
            <span className="stat-label">TOTAL USED</span>
            <span className="stat-value">{loading ? '...' : stats.totalUsed.toFixed(2)} GB</span>
          </div>
        </div>
      </div>

      {/* My Packages Section */}
      <div className="my-packages-section">
        <div className="section-header">
          <h3>MY PACKAGES</h3>
          <div className="status-badges">
            {stats.activeCount > 0 && <span className="active-badge">{stats.activeCount} ACTIVE</span>}
            {stats.pausedCount > 0 && <span className="paused-badge">{stats.pausedCount} PAUSED</span>}
          </div>
        </div>

        <div className="my-packages-grid">
          {loading ? (
            <div className="loading-message">Loading packages...</div>
          ) : packages.length === 0 ? (
            <div className="empty-message">
              No packages. Purchase a package below to get started.
            </div>
          ) : (
            packages.map((pkg) => {
              const percentage = (parseFloat(pkg.remaining_gb) / parseFloat(pkg.total_gb)) * 100;

              return (
                <div key={pkg.id} className="my-package-card">
                  <div className="package-card-header">
                    <div className="package-icon">
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                        <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"/>
                      </svg>
                    </div>
                    <div className="package-info">
                      <div className="package-name-row">
                        <span className="package-name">{pkg.name}</span>
                        <span className={`status-badge ${pkg.status}`}>{pkg.status.toUpperCase()}</span>
                      </div>
                      <span className="package-type">{pkg.proxy_type.replace('_', ' ').toUpperCase()}</span>
                    </div>
                  </div>

                  <div className="package-remaining">
                    <span className="remaining-label">REMAINING</span>
                    <div className="remaining-row">
                      <span className="remaining-value">
                        {parseFloat(pkg.remaining_gb).toFixed(2)}<span className="unit">GB</span>
                      </span>
                      <span className="remaining-percent">{percentage.toFixed(2)}%</span>
                    </div>
                    <div className="remaining-progress">
                      <div className="progress-fill" style={{width: `${percentage}%`}}></div>
                    </div>
                    <span className="total-bandwidth">TOTAL BANDWIDTH: {parseFloat(pkg.total_gb).toFixed(2)}GB</span>
                  </div>

                  <button
                    className="topup-btn"
                    onClick={() => openPurchaseModal(pkg.proxy_type)}
                  >
                    TOP-UP BANDWIDTH
                  </button>

                  <div className="package-actions">
                    <button
                      className="action-btn usage"
                      onClick={() => navigate(`/dashboard/rotating/${pkg.proxy_type === 'residential_premium' ? 'residential-premium' : 'residential-basic'}`)}
                    >
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83"/>
                      </svg>
                      GENERATE
                    </button>
                    <button
                      className="action-btn pause"
                      onClick={() => handlePauseResume(pkg)}
                    >
                      {pkg.status === 'active' ? 'PAUSE' : 'RESUME'}
                    </button>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>

      {/* Available Packages Section */}
      <div className="available-packages-section">
        <div className="section-header">
          <h3>AVAILABLE PACKAGES</h3>
          <span className="section-subtitle">CHOOSE A PACKAGE TO GET STARTED</span>
        </div>

        <div className="available-packages-grid">
          {availablePackages.map((pkg) => {
            const isMonthly = pkg.isMonthly;
            const tiers = isMonthly ? monthlyPricingTiers : getTiersForPackage(pkg.type);

            return (
              <div key={pkg.type} className="available-package-card">
                <div className="package-card-top">
                  <div className="package-icons">
                    <div className="icon-box">
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        {isMonthly ? (
                          <><rect x="3" y="4" width="18" height="18" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></>
                        ) : (
                          <><rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></>
                        )}
                      </svg>
                    </div>
                    <div className="shield-icon">
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                        <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
                      </svg>
                    </div>
                  </div>
                </div>

                <h4 className="available-package-name">{pkg.name}</h4>
                <span className="available-package-type">{pkg.displayType}</span>
                <p className="available-package-desc">{pkg.description}</p>

                <div className="pricing-tiers-section">
                  <h5>{isMonthly ? 'PRICING TIERS (1 DAY)' : 'PRICING TIERS'}</h5>
                  <div className="tiers-list">
                    {pricingLoading && !isMonthly ? (
                      <div className="loading-text">Loading...</div>
                    ) : tiers.length === 0 ? (
                      <div className="no-tiers">No pricing available</div>
                    ) : (
                      tiers.map((tier, tierIndex) => (
                        <div key={tierIndex} className="tier-item">
                          <span className="tier-range">{formatTierRange(tier)}</span>
                          <span className="tier-price">
                            ${isMonthly ? tier.d1.toFixed(2) : tier.pricePerGb.toFixed(2)}<span>/GB</span>
                          </span>
                        </div>
                      ))
                    )}
                  </div>
                </div>

                <button
                  className="purchase-package-btn"
                  onClick={() => openPurchaseModal(pkg.type)}
                >
                  PURCHASE PACKAGE
                </button>
              </div>
            );
          })}
        </div>
      </div>

      {/* Purchase Modal */}
      {showPurchaseModal && selectedPackageType && (
        <div className="purchase-modal-overlay" onMouseDown={(e) => {
          if (e.target === e.currentTarget) {
            setShowPurchaseModal(false);
          }
        }}>
          <div className="purchase-modal">
            {/* Header */}
            <div className="purchase-modal-header">
              <div className="purchase-header-left">
                <div className="purchase-header-icon">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <line x1="12" y1="5" x2="12" y2="19"/>
                    <line x1="5" y1="12" x2="19" y2="12"/>
                  </svg>
                </div>
                <div className="purchase-header-text">
                  <h3>PURCHASE PACKAGE</h3>
                  <p>PURCHASE A NEW PACKAGE</p>
                </div>
              </div>
              <button className="purchase-modal-close" onClick={() => setShowPurchaseModal(false)}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="18" y1="6" x2="6" y2="18"/>
                  <line x1="6" y1="6" x2="18" y2="18"/>
                </svg>
              </button>
            </div>

            {/* Duration Selector (monthly only) */}
            {selectedPackageType === 'monthly_residential' && (
              <div className="purchase-duration-section">
                <span className="purchase-amount-label">DURATION</span>
                <div className="purchase-duration-tabs">
                  {durationOptions.map((d) => (
                    <button
                      key={d.key}
                      className={`purchase-duration-tab ${selectedDuration === d.key ? 'active' : ''}`}
                      onClick={() => setSelectedDuration(d.key)}
                    >
                      {d.label}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Amount Input */}
            <div className="purchase-amount-section">
              <div className="purchase-amount-header">
                <span className="purchase-amount-label">AMOUNT TO ADD</span>
              </div>
              <div className="purchase-amount-input-wrapper">
                <input
                  type="number"
                  min="1"
                  max="10000"
                  value={purchaseInputValue}
                  onChange={handlePurchaseInputChange}
                  onBlur={handlePurchaseInputBlur}
                  className="purchase-amount-input"
                />
                <span className="purchase-amount-suffix">GB</span>
              </div>
            </div>

            {/* Pricing Tiers */}
            <div className="purchase-pricing-section">
              <span className="purchase-pricing-label">PRICING</span>
              <div className="purchase-pricing-list">
                {selectedPackageType === 'monthly_residential' ? (
                  monthlyPricingTiers.map((tier, index) => {
                    const isNoExpiry = selectedDuration === 'no_expiry';
                    const priceKey = isNoExpiry ? null : selectedDuration;
                    const payg = calculatePrice('residential_basic', tier.minGb);
                    const price = isNoExpiry ? payg.pricePerGb : tier[priceKey];
                    return (
                      <div
                        key={index}
                        className={`purchase-tier-item ${isActiveTier(tier, purchaseAmount) ? 'active' : ''}`}
                      >
                        <div className="purchase-tier-indicator">
                          <div className="tier-dot"></div>
                        </div>
                        <span className="purchase-tier-range">{formatTierRange(tier)}</span>
                        <span className="purchase-tier-price">${price.toFixed(4)}<span>/GB</span></span>
                      </div>
                    );
                  })
                ) : pricingLoading ? (
                  <div className="purchase-loading">Loading pricing...</div>
                ) : (
                  getExtendedTiers(selectedPackageType).map((tier, index) => (
                    <div
                      key={index}
                      className={`purchase-tier-item ${isActiveTier(tier, purchaseAmount) ? 'active' : ''}`}
                    >
                      <div className="purchase-tier-indicator">
                        <div className="tier-dot"></div>
                      </div>
                      <span className="purchase-tier-range">{formatTierRange(tier)}</span>
                      <span className="purchase-tier-price">${tier.pricePerGb.toFixed(4)}<span>/GB</span></span>
                    </div>
                  ))
                )}
              </div>
            </div>

            {/* Total Cost */}
            <div className="purchase-total-section">
              <div className="purchase-total-left">
                <span className="purchase-total-label">TOTAL COST</span>
                <span className="purchase-total-amount">
                  <span className="currency">$</span>
                  {(() => {
                    if (selectedPackageType === 'monthly_residential') {
                      if (selectedDuration === 'no_expiry') {
                        return calculatePrice('residential_basic', purchaseAmount).totalPrice?.toFixed(2) || '0.00';
                      }
                      const p = getMonthlyPrice(purchaseAmount, selectedDuration);
                      return (p * purchaseAmount).toFixed(2);
                    }
                    return calculatePrice(selectedPackageType, purchaseAmount).totalPrice?.toFixed(2) || '0.00';
                  })()}
                </span>
              </div>
              <div className="purchase-total-right">
                <span className="purchase-per-gb">
                  ${(() => {
                    if (selectedPackageType === 'monthly_residential') {
                      if (selectedDuration === 'no_expiry') {
                        return calculatePrice('residential_basic', purchaseAmount).pricePerGb?.toFixed(4) || '0.0000';
                      }
                      return getMonthlyPrice(purchaseAmount, selectedDuration).toFixed(4);
                    }
                    return calculatePrice(selectedPackageType, purchaseAmount).pricePerGb?.toFixed(4) || '0.0000';
                  })()}/GB
                </span>
              </div>
            </div>

            {/* Error Message */}
            {purchaseError && (
              <div className="purchase-error">
                {purchaseError}
              </div>
            )}

            {/* Footer Buttons */}
            <div className="purchase-modal-footer">
              <button
                className="purchase-cancel-btn"
                onClick={() => setShowPurchaseModal(false)}
                disabled={isPurchasing}
              >
                CANCEL
              </button>
              <button
                className="purchase-confirm-btn"
                onClick={handlePurchase}
                disabled={isPurchasing || purchaseAmount < 1}
              >
                {isPurchasing ? 'CREATING PAYMENT...' : 'PAY WITH CRYPTO'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BandwidthPackagesPage;
